import { LINEAR_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const DLA_PARAMS = [
  {
    kind: 'int',
    key: 'particles',
    label: 'Partikel',
    min: 100,
    max: 60000,
    step: 1,
    scale: 'log',
    default: 9000,
    bulkRange: [1500, 25000],
    group: 'Aggregation',
    tooltip: 'Wie viele Wanderpartikel insgesamt angelagert werden sollen.',
  },
  {
    kind: 'float',
    key: 'stepSize',
    label: 'Schrittgrösse',
    min: 0.5,
    max: 12,
    step: 0.1,
    default: 2.2,
    unit: 'px',
    group: 'Aggregation',
  },
  {
    kind: 'float',
    key: 'stickiness',
    label: 'Klebewahrscheinlichkeit',
    min: 0.02,
    max: 1,
    step: 0.01,
    default: 0.85,
    group: 'Aggregation',
    tooltip: 'Niedrige Werte lassen Partikel weiterlaufen — das ergibt kompaktere, weniger fraktale Formen.',
  },
  {
    kind: 'enum',
    key: 'seedMode',
    label: 'Keimstruktur',
    default: 'outline',
    group: 'Aggregation',
    options: [
      { value: 'outline', label: 'Textkontur' },
      { value: 'points', label: 'Einzelne Punkte' },
      { value: 'border', label: 'Canvas-Rand' },
    ],
  },
  {
    kind: 'int',
    key: 'seedPoints',
    label: 'Keimpunkte',
    min: 1,
    max: 200,
    step: 1,
    default: 8,
    group: 'Aggregation',
    showIf: (v) => v.seedMode === 'points',
  },
  {
    kind: 'enum',
    key: 'direction',
    label: 'Wachstumsrichtung',
    default: 'outward',
    segmented: true,
    group: 'Aggregation',
    options: [
      { value: 'outward', label: 'Nach aussen' },
      { value: 'inward', label: 'Nach innen' },
    ],
  },
  {
    kind: 'bool',
    key: 'textBlocks',
    label: 'Text als Sperrzone',
    default: true,
    group: 'Aggregation',
    tooltip: 'Aus: Partikel dürfen die Buchstaben durchwandern und darin anwachsen.',
  },
  {
    kind: 'int',
    key: 'perStep',
    label: 'Partikel pro Frame',
    min: 20,
    max: 3000,
    step: 10,
    default: 400,
    neverRandomise: true,
    group: 'Aggregation',
  },

  {
    kind: 'range',
    key: 'radius',
    label: 'Partikelradius',
    min: 0.4,
    max: 20,
    step: 0.1,
    default: [1.2, 3.4],
    unit: 'px',
    precision: 1,
    group: 'Darstellung',
  },
  {
    kind: 'curve',
    key: 'radiusOverTime',
    label: 'Radius über die Zeit',
    default: LINEAR_CURVE,
    xLabel: 'früh',
    yLabel: 'spät',
    group: 'Darstellung',
  },
  {
    kind: 'bool',
    key: 'connect',
    label: 'Mit dem Anlagerungspunkt verbinden',
    default: true,
    group: 'Darstellung',
    tooltip: 'Zeichnet den Ast statt nur den Punkt — macht die Verästelung sichtbar.',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.9,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'time',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'time', label: 'Nach Anlagerungszeit' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'depth', label: 'Nach Astordnung' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'ink' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type DlaValues = SchemaValues<typeof DLA_PARAMS>;
